import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { TOURS, getTourForPath } from './tourScripts';
import { subscribe } from './tourEventBus';

const TourContext = createContext(null);

function matchTriggerPath(triggerPath, pathname) {
  if (triggerPath === pathname) return true;
  if (triggerPath === '/workout/' && pathname.startsWith('/workout/')) return true;
  return false;
}

export function TourProvider({ children }) {
  const { user } = useAuth();
  const { progress, loaded, setTourState } = useTutorial();
  const location = useLocation();

  const [activeTour, setActiveTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  // Resume cache: { [tourId]: stepIndex } in memory only.
  const resumeRef = useRef({});

  // Refs that mirror state — safe to read inside callbacks without stale-closure
  const stepIndexRef = useRef(0);
  const activeTourRef = useRef(null);
  useEffect(() => { stepIndexRef.current = stepIndex; }, [stepIndex]);
  useEffect(() => { activeTourRef.current = activeTour; }, [activeTour]);

  const advance = useCallback(() => {
    // Read current tour + step synchronously via refs (avoids stale-closure issues)
    const curTour = activeTourRef.current;
    if (!curTour) return;
    const idx = stepIndexRef.current;
    const step = curTour.steps[idx];
    const nextIdx = idx + 1;

    if (step.onAdvance === 'complete' || nextIdx >= curTour.steps.length) {
      setTourState(curTour.id, 'completed');
      delete resumeRef.current[curTour.id];
      stepIndexRef.current = 0;
      setStepIndex(0);
      setActiveTour(null);
    } else {
      stepIndexRef.current = nextIdx;
      setStepIndex(nextIdx);
    }
  }, [setTourState]);

  const skip = useCallback(() => {
    const curTour = activeTourRef.current;
    if (!curTour) return;
    setTourState(curTour.id, 'dismissed');
    delete resumeRef.current[curTour.id];
    stepIndexRef.current = 0;
    setStepIndex(0);
    setActiveTour(null);
  }, [setTourState]);

  // Trigger logic — fires on route changes and on progress load
  useEffect(() => {
    if (!user || user.role !== 'client' || !loaded || !progress) return;

    if (activeTour) {
      // Pause if route no longer matches the active tour's trigger path
      if (!matchTriggerPath(activeTour.triggerPath, location.pathname)) {
        resumeRef.current[activeTour.id] = stepIndexRef.current;
        stepIndexRef.current = 0;
        setStepIndex(0);
        setActiveTour(null);
      }
      return;
    }

    const candidate = getTourForPath(location.pathname);
    if (!candidate) return;
    if (progress[candidate.id]?.state !== 'pending') return;

    // 400ms settle delay so the page has time to mount its anchors
    const tid = setTimeout(() => {
      const resumeIdx = resumeRef.current[candidate.id] || 0;
      stepIndexRef.current = resumeIdx;
      setStepIndex(resumeIdx);
      setActiveTour(candidate);
    }, 400);
    return () => clearTimeout(tid);
  }, [location.pathname, user, loaded, progress, activeTour]);

  // Subscribe to custom-gate events for the current step
  useEffect(() => {
    if (!activeTour) return;
    const step = activeTour.steps[stepIndex];
    if (!step || step.gate.type !== 'custom') return;
    const unsub = subscribe(step.gate.event, (payload) => {
      if (step.gate.condition(payload)) advance();
    });
    return unsub;
  }, [activeTour, stepIndex, advance]);

  return (
    <TourContext.Provider value={{ activeTour, stepIndex, advance, skip }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) return { activeTour: null, stepIndex: 0, advance: () => {}, skip: () => {} };
  return ctx;
}
